import type { Category, DriveNode } from './types';

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  const shown = v >= 100 || i === 0 ? String(Math.round(v)) : v.toFixed(1).replace(/\.0$/, '');
  return `${shown} ${units[i]}`;
}

const rtf = typeof Intl !== 'undefined' && 'RelativeTimeFormat' in Intl ? new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }) : null;

/** "just now", "2 minutes ago", "yesterday", then a date. */
export function when(iso: string): string {
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  // Anything in the last minute reads the same everywhere (the list and the details said "now" and "6 seconds ago" for one upload, P2-15).
  if (s < 60) return 'just now';
  if (rtf && s < 3600) return rtf.format(-Math.round(s / 60), 'minute');
  if (rtf && s < 86400) return rtf.format(-Math.round(s / 3600), 'hour');
  if (rtf && s < 7 * 86400) return rtf.format(-Math.round(s / 86400), 'day');
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}

export function fullDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' });
}

export const TYPE_LABEL: Record<Category, string> = {
  folder: 'Folder', image: 'Picture', pdf: 'PDF', video: 'Video', audio: 'Audio', archive: 'Archive',
  document: 'Document', spreadsheet: 'Spreadsheet', presentation: 'Presentation', code: 'Code', text: 'Text', other: 'File',
};

export type SortKey = 'name' | 'modified' | 'size' | 'type';

export function sortNodes(list: DriveNode[], key: SortKey, desc: boolean): DriveNode[] {
  const coll = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  const dir = desc ? -1 : 1;
  return [...list].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
    let c = 0;
    if (key === 'name') c = coll.compare(a.name, b.name);
    else if (key === 'modified') c = a.modifiedAt < b.modifiedAt ? -1 : a.modifiedAt > b.modifiedAt ? 1 : 0;
    else if (key === 'size') c = a.size - b.size;
    else c = coll.compare(TYPE_LABEL[a.type], TYPE_LABEL[b.type]) || coll.compare(a.name, b.name);
    return (c || coll.compare(a.name, b.name)) * dir;
  });
}

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const MOD = isMac ? '⌘' : 'Ctrl';
